import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// API base URL - defaults to localhost:8000 if not set
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Generate a UUID v4 for session ID
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

function Chat({ token, onLogout }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m your Nimble AI assistant. Ask me anything about your business data - customers, orders, formulations, suppliers, and more!' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => generateUUID());
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const renderMessage = (msg, i) => {
    if (msg.role === 'assistant') {
      const displayType = msg.data?.display_type || 'none';
      
      return (
        <div key={i} className="chat-msg assistant">
          <b>AI:</b>
          <div className="markdown-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {msg.content}
            </ReactMarkdown>
          </div>
          {msg.metadata?.sql_query && (
            <details className="sql-details">
              <summary>View SQL Query</summary>
              <pre>{msg.metadata.sql_query}</pre>
            </details>
          )}
          {displayType === 'table' && msg.data && <DataTable data={msg.data} />}
          {displayType === 'bar_chart' && msg.data && <DataBarChart data={msg.data} />}
          {displayType === 'pie_chart' && msg.data && <DataPieChart data={msg.data} />}
          {displayType === 'line_chart' && msg.data && <DataLineChart data={msg.data} />}
        </div>
      );
    }
    return (
      <div key={i} className="chat-msg user">
        <b>You:</b> {msg.content}
      </div>
    );
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const userMsg = { role: 'user', content: input };
    setMessages(msgs => [...msgs, userMsg]);
    const currentInput = input;
    setInput('');
    setLoading(true);
    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      // Only add Authorization header if token exists and isn't the demo token
      if (token && token !== 'demo-token') {
        headers['Authorization'] = 'Bearer ' + token;
      }
      
      const res = await fetch(API_BASE_URL + '/api/v1/chat/message', {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: currentInput, session_id: sessionId })
      });
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Request failed');
      }
      
      setMessages(msgs => [...msgs, { 
        role: 'assistant', 
        content: data.content || data.response || 'No response.', 
        data: data.data,
        metadata: data.metadata
      }]);
    } catch (err) {
      setMessages(msgs => [...msgs, { 
        role: 'assistant', 
        content: `Error: ${err.message || 'Error contacting server.'}` 
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <span>🤖 Nimble AI Chat</span>
        <button onClick={onLogout} style={{ opacity: token === 'demo-token' ? 0.5 : 1 }}>Logout</button>
      </div>
      <div className="chat-messages">
        {messages.map(renderMessage)}
        {loading && (
          <div className="chat-msg assistant loading">
            <b>AI:</b> <span className="typing-indicator">Thinking...</span>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
      <form className="chat-input" onSubmit={sendMessage}>
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Ask about customers, orders, formulations..."
          disabled={loading}
        />
        <button type="submit" disabled={loading || !input.trim()}>{loading ? '...' : 'Send'}</button>
      </form>
    </div>
  );
}

// Convert snake_case or camelCase column names to user-friendly Title Case
const formatColumnName = (col) => {
  if (!col) return '';
  return col
    .replace(/_/g, ' ')           // snake_case to spaces
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase to spaces
    .replace(/\b\w/g, c => c.toUpperCase()) // capitalize each word
    .replace(/\bId\b/g, 'ID')     // ID uppercase
    .replace(/\bSql\b/g, 'SQL')   // SQL uppercase
    .replace(/\bUrl\b/g, 'URL')   // URL uppercase
    .trim();
};

function DataTable({ data }) {
  if (!data || !data.columns || !data.rows) return null;

  const isWide = data.columns.length > 5;
  const isTall = data.rows.length > 5;
  const needsScroll = isWide || isTall;

  const table = (
    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isWide ? data.columns.length * 140 : undefined }}>
      <thead>
        <tr>
          {data.columns.map(col => (
            <th key={col} style={{
              borderBottom: '2px solid #ccc',
              textAlign: 'left',
              padding: '6px 8px',
              whiteSpace: 'nowrap',
              background: '#f5f5f5',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>{formatColumnName(col)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.rows.map((row, i) => (
          <tr key={i}>
            {data.columns.map(col => (
              <td key={col} style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>
                {row[col] != null ? String(row[col]) : ''}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (needsScroll) {
    return (
      <div style={{
        maxHeight: 320,
        overflowY: 'auto',
        overflowX: 'auto',
        margin: '12px 0',
        border: '1px solid #e0e0e0',
        borderRadius: 6
      }}>
        {table}
      </div>
    );
  }

  return <div style={{ margin: '12px 0' }}>{table}</div>;
}

function DataBarChart({ data }) {
  if (!data || !data.columns || !data.rows || data.rows.length === 0) return null;
  
  // Find numeric column for Y-axis
  const numericCol = data.columns.find(col => {
    return data.rows.some(row => {
      const val = row[col];
      return val != null && !isNaN(Number(val));
    });
  });
  
  if (!numericCol) return null;
  
  // Find non-numeric column for X-axis (label)
  const labelCol = data.columns.find(col => col !== numericCol) || data.columns[0];
  
  // Find max value for scaling
  const maxVal = Math.max(...data.rows.map(row => Number(row[numericCol]) || 0));
  
  const colors = ['#3366cc', '#dc3912', '#ff9900', '#109618', '#990099', '#0099c6', '#dd4477', '#66aa00', '#b82e2e', '#316395'];
  const chartHeight = 180;
  
  return (
    <div style={{ marginTop: 16, padding: 16, background: '#f9f9f9', borderRadius: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 12, textAlign: 'center' }}>{numericCol}</div>
      
      {/* Chart area */}
      <div style={{ display: 'flex', alignItems: 'flex-end', height: chartHeight, gap: 8, borderBottom: '2px solid #ccc', paddingBottom: 4 }}>
        {data.rows.map((row, idx) => {
          const value = Number(row[numericCol]) || 0;
          const height = maxVal > 0 ? (value / maxVal) * (chartHeight - 30) : 0;
          const color = colors[idx % colors.length];
          return (
            <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 30 }}>
              <div style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 4, color: '#333' }}>
                {value >= 1000 ? `${(value/1000).toFixed(1)}k` : value.toLocaleString()}
              </div>
              <div style={{ 
                width: '100%', 
                maxWidth: 50,
                height: Math.max(height, 4), 
                background: color, 
                borderRadius: '4px 4px 0 0',
                transition: 'height 0.3s ease'
              }} />
            </div>
          );
        })}
      </div>
      
      {/* X-axis labels */}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        {data.rows.map((row, idx) => {
          const label = row[labelCol] != null ? String(row[labelCol]) : `Item ${idx + 1}`;
          return (
            <div key={idx} style={{ 
              flex: 1, 
              fontSize: 10, 
              textAlign: 'center', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 30,
              color: '#666'
            }}>
              {label.length > 12 ? label.slice(0, 10) + '...' : label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DataPieChart({ data }) {
  if (!data || !data.columns || !data.rows || data.rows.length === 0) return null;
  
  const numericCol = data.columns.find(col => {
    return data.rows.some(row => {
      const val = row[col];
      return val != null && !isNaN(Number(val));
    });
  });
  
  if (!numericCol) return null;
  
  const labelCol = data.columns.find(col => col !== numericCol) || data.columns[0];
  const total = data.rows.reduce((sum, row) => sum + (Number(row[numericCol]) || 0), 0);
  
  const colors = ['#3366cc', '#dc3912', '#ff9900', '#109618', '#990099', '#0099c6', '#dd4477', '#66aa00', '#b82e2e', '#316395'];
  
  // Calculate pie slices as conic-gradient stops
  let currentAngle = 0;
  const slices = data.rows.map((row, idx) => {
    const value = Number(row[numericCol]) || 0;
    const percent = total > 0 ? (value / total) * 100 : 0;
    const startAngle = currentAngle;
    currentAngle += percent;
    return {
      label: row[labelCol] != null ? String(row[labelCol]) : `Item ${idx + 1}`,
      value,
      percent,
      color: colors[idx % colors.length],
      startAngle,
      endAngle: currentAngle
    };
  });
  
  // Build conic-gradient string
  const gradientStops = slices.map((slice, idx) => {
    return `${slice.color} ${slice.startAngle}% ${slice.endAngle}%`;
  }).join(', ');
  
  return (
    <div style={{ marginTop: 16, padding: 16, background: '#f9f9f9', borderRadius: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 12, textAlign: 'center' }}>
        Distribution of {numericCol}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        {/* Pie Chart */}
        <div style={{
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: `conic-gradient(${gradientStops})`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          flexShrink: 0
        }} />
        
        {/* Legend */}
        <div style={{ flex: 1, minWidth: 150 }}>
          {slices.map((slice, idx) => (
            <div key={idx} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: 6,
              fontSize: 12
            }}>
              <div style={{ 
                width: 14, 
                height: 14, 
                background: slice.color, 
                borderRadius: 3, 
                marginRight: 8,
                flexShrink: 0
              }} />
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 500 }}>{slice.label}</span>
                <span style={{ color: '#666', marginLeft: 4 }}>
                  ${slice.value.toLocaleString()} ({slice.percent.toFixed(1)}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Total */}
      <div style={{ 
        marginTop: 12, 
        paddingTop: 8, 
        borderTop: '1px solid #ddd', 
        fontSize: 12, 
        fontWeight: 'bold',
        textAlign: 'center'
      }}>
        Total: ${total.toLocaleString()}
      </div>
    </div>
  );
}

function DataLineChart({ data }) {
  if (!data || !data.columns || !data.rows || data.rows.length === 0) return null;
  
  const numericCol = data.columns.find(col => {
    return data.rows.some(row => {
      const val = row[col];
      return val != null && !isNaN(Number(val));
    });
  });
  
  if (!numericCol) return null;
  
  const labelCol = data.columns.find(col => col !== numericCol) || data.columns[0];
  const values = data.rows.map(row => Number(row[numericCol]) || 0);
  const maxVal = Math.max(...values);
  const minVal = Math.min(...values);
  
  return (
    <div style={{ marginTop: 16, padding: 8, background: '#f9f9f9', borderRadius: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>Trend: {numericCol}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', height: 100, gap: 4, borderBottom: '1px solid #ccc' }}>
        {data.rows.map((row, idx) => {
          const value = Number(row[numericCol]) || 0;
          const height = maxVal > minVal ? ((value - minVal) / (maxVal - minVal)) * 80 + 20 : 50;
          return (
            <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: 10, marginBottom: 2 }}>{value}</div>
              <div style={{ width: '100%', height: `${height}%`, background: '#007bff', borderRadius: '2px 2px 0 0' }}></div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
        {data.rows.map((row, idx) => (
          <div key={idx} style={{ flex: 1, fontSize: 9, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row[labelCol] != null ? String(row[labelCol]).slice(0, 8) : idx + 1}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Chat;
export { DataTable, DataBarChart, DataPieChart, DataLineChart };
